import React, { useEffect, useState } from "react";
import { SegmentedController, useI18nContext } from "@procore/core-react";
import { Widget } from "@bryntum/core-thin";
import { getBoardsConfigurePanelValues, updateBoardsConfigurePanelLocalStorage } from ".";
import type { BoardsConfigPanelValues } from ".";
import "./index.css";

type TaskCardDetailMode = BoardsConfigPanelValues["taskCardDetailMode"];

interface TaskCardDetailsSegmentedControllerProps {
   uniqueSuffix: string;
}

const RESOURCE_TOGGLE_IDS = [
   "boards-configure-employee-id",
   "boards-configure-job-title",
   "boards-configure-phone-number",
   "boards-configure-total-assignment-range",
   "boards-configure-allocation-information",
   "boards-configure-rate-info",
   "boards-configure-address",
   "boards-configure-resource-photo",
] as const;

const COMPACT_ACTIVE_KEYS: ReadonlySet<keyof BoardsConfigPanelValues> = new Set([
   "showJobTitle",
   "showTotalAssignmentRange",
]);

const RESOURCE_KEY_BY_TOGGLE_ID: Record<string, keyof BoardsConfigPanelValues> = {
   "boards-configure-employee-id": "showEmployeeID",
   "boards-configure-job-title": "showJobTitle",
   "boards-configure-phone-number": "showPhoneNumber",
   "boards-configure-total-assignment-range": "showTotalAssignmentRange",
   "boards-configure-allocation-information": "showAllocationInformation",
   "boards-configure-rate-info": "showRateInfo",
   "boards-configure-address": "showAddress",
   "boards-configure-resource-photo": "showResourcePhoto",
};

function getToggleStatesForMode(
   mode: TaskCardDetailMode,
): Record<keyof BoardsConfigPanelValues, boolean> {
   const isExpanded = mode === "expanded";
   return {
      showEmployeeID: isExpanded,
      showJobTitle: isExpanded || COMPACT_ACTIVE_KEYS.has("showJobTitle"),
      showPhoneNumber: isExpanded,
      showTags: isExpanded,
      showAllTags: false,
      showTotalAssignmentRange: isExpanded || COMPACT_ACTIVE_KEYS.has("showTotalAssignmentRange"),
      showAllocationInformation: isExpanded,
      showRateInfo: isExpanded,
      showAddress: isExpanded,
      showResourcePhoto: isExpanded,
   } as Record<keyof BoardsConfigPanelValues, boolean>;
}

function syncBryntumToggle(toggleId: string, suffix: string, checked: boolean) {
   const widget = Widget.getById(`${toggleId}${suffix}`);
   if (widget && "checked" in widget) {
      (widget as Widget & { checked: boolean }).checked = checked;
   }
}

export default function TaskCardDetailsSegmentedController({
   uniqueSuffix,
}: TaskCardDetailsSegmentedControllerProps) {
   const I18n = useI18nContext();
   const [selectedMode, setSelectedMode] = useState<TaskCardDetailMode>("compact");

   useEffect(() => {
      const storedMode = getBoardsConfigurePanelValues().taskCardDetailMode;
      if (storedMode) {
         setSelectedMode(storedMode);
      }
   }, []);

   const handleChange = (mode: TaskCardDetailMode) => {
      setSelectedMode(mode);
      updateBoardsConfigurePanelLocalStorage("taskCardDetailMode", mode);

      const toggleStates = getToggleStatesForMode(mode);

      for (const toggleId of RESOURCE_TOGGLE_IDS) {
         const key = RESOURCE_KEY_BY_TOGGLE_ID[toggleId];
         const value = toggleStates[key] ?? false;
         updateBoardsConfigurePanelLocalStorage(key, value);
         syncBryntumToggle(toggleId, uniqueSuffix, value);
      }

      updateBoardsConfigurePanelLocalStorage("showTags", toggleStates.showTags);
      updateBoardsConfigurePanelLocalStorage("showAllTags", toggleStates.showAllTags);
   };

   return (
      <SegmentedController
         className={`custom-task-card-details-segmented-controller ${selectedMode}`}
      >
         <SegmentedController.Segment
            onClick={() => handleChange("compact")}
            selected={selectedMode === "compact"}
         >
            {I18n.t("views.company.workforce_planning.boards.configure_panel.compact")}
         </SegmentedController.Segment>
         <SegmentedController.Segment
            onClick={() => handleChange("expanded")}
            selected={selectedMode === "expanded"}
         >
            {I18n.t("views.company.workforce_planning.boards.configure_panel.expanded")}
         </SegmentedController.Segment>
      </SegmentedController>
   );
}
